import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Instagram, Youtube, Twitter, Check } from "lucide-react";
import { LogoMark } from "./Nav";
import { Field, inputClasses } from "./GetFeatured";
import ContributeWriter from "./ContributeWriter";
import { NAV_LINKS } from "../../data/content";
import { scrollToId } from "../../lib/lenis";
import { api, YOUTUBE_CHANNEL } from "../../lib/api";

export default function Footer() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) || form.message.trim().length < 5) {
      setError("Name, valid email and a message are required.");
      return;
    }
    setError("");
    setStatus("loading");
    try {
      await api.sendInquiry({ type: "contact", ...form });
      setStatus("success");
    } catch {
      setStatus("error");
      setError("Message glitched. Try again.");
    }
  };

  return (
    <footer id="contact" className="relative bg-wood border-t border-woodborder" data-testid="footer">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24">
        <div className="grid lg:grid-cols-2 gap-14">
          <div>
            <div className="flex items-center gap-3">
              <LogoMark size={44} />
              <span className="font-display text-2xl tracking-wide text-cream pt-1">
                THE <span className="text-burnt">COBAIN</span> REPORT
              </span>
            </div>
            <p className="mt-6 text-sand font-light text-sm max-w-sm leading-relaxed">
              Detroit's urban culture media property. Podcast, music, editorial, commerce — bigger than a podcast, built for the culture.
            </p>
            <nav className="mt-8 flex flex-wrap gap-x-6 gap-y-3" data-testid="footer-nav">
              {NAV_LINKS.map((l) => (
                <button key={l.id} onClick={() => (l.id === "get-featured" ? navigate("/get-featured") : scrollToId(l.id))} className="font-mono text-[10px] tracking-[0.22em] text-clay hover:text-tan transition-colors" data-testid={`footer-link-${l.id}`}>
                  {l.label}
                </button>
              ))}
              <button onClick={() => scrollToId("team")} className="font-mono text-[10px] tracking-[0.22em] text-clay hover:text-tan transition-colors" data-testid="footer-link-team">
                MEET THE TEAM
              </button>
              <button onClick={() => scrollToId("partner")} className="font-mono text-[10px] tracking-[0.22em] text-clay hover:text-tan transition-colors" data-testid="footer-link-partner">
                PARTNER WITH COBAIN
              </button>
              <ContributeWriter variant="link" />
            </nav>
            <div className="mt-8 flex items-center gap-3" data-testid="footer-socials">
              <a href="https://www.instagram.com/thecobainreport" target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="footer-social-instagram"
                className="w-10 h-10 rounded-full border border-woodborder flex items-center justify-center text-sand hover:text-burnt hover:border-burnt transition-colors">
                <Instagram size={16} />
              </a>
              <a href={YOUTUBE_CHANNEL} target="_blank" rel="noopener noreferrer" aria-label="YouTube" data-testid="footer-social-youtube"
                className="w-10 h-10 rounded-full border border-woodborder flex items-center justify-center text-sand hover:text-burnt hover:border-burnt transition-colors">
                <Youtube size={16} />
              </a>
              <a href="https://x.com/thecobainreport" target="_blank" rel="noopener noreferrer" aria-label="X" data-testid="footer-social-x"
                className="w-10 h-10 rounded-full border border-woodborder flex items-center justify-center text-sand hover:text-burnt hover:border-burnt transition-colors">
                <Twitter size={16} />
              </a>
            </div>
          </div>

          <div>
            <p className="font-mono text-[10px] tracking-[0.3em] text-tan mb-6">GENERAL CONTACT</p>
            {status === "success" ? (
              <div className="border border-gold/40 bg-card/60 p-8 flex items-center gap-4" data-testid="contact-success">
                <span className="w-10 h-10 rounded-full bg-gold flex items-center justify-center shrink-0">
                  <Check size={18} className="text-obsidian" />
                </span>
                <p className="font-editorial font-bold text-cream">Message received. The team will get back to you.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="contact-form" noValidate>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="NAME" testId="ct-name">
                    <input className={inputClasses} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" data-testid="ct-name-input" />
                  </Field>
                  <Field label="EMAIL" testId="ct-email">
                    <input className={inputClasses} type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@email.com" data-testid="ct-email-input" />
                  </Field>
                </div>
                <Field label="MESSAGE" testId="ct-message">
                  <textarea className={`${inputClasses} min-h-[100px] resize-y`} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="What's on your mind?" data-testid="ct-message-textarea" />
                </Field>
                {error && <p className="font-mono text-[11px] text-burnt-hover" data-testid="ct-form-error">{error}</p>}
                <button
                  type="submit"
                  disabled={status === "loading"}
                  data-testid="ct-submit"
                  className="font-mono text-[11px] tracking-[0.22em] border border-tan/40 hover:border-burnt hover:bg-burnt/10 text-cream px-8 py-4 rounded-full transition-all duration-300 disabled:opacity-60"
                >
                  {status === "loading" ? "SENDING..." : "SEND MESSAGE"}
                </button>
              </form>
            )}
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-woodborder flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="font-mono text-[10px] tracking-[0.2em] text-clay" data-testid="footer-copyright">
            © 2026 THE COBAIN REPORT — DETROIT, MI. ALL RIGHTS RESERVED.
          </p>
          <p className="font-mono text-[10px] tracking-[0.2em] text-clay">BUILT FOR THE CULTURE.</p>
        </div>
      </div>
    </footer>
  );
}
